import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { AccessTokenDto, Role } from "../services/auth/auth.dto";
import { useToast } from "./toast";

interface User {
  id: string;
  email: string;
  role: Role;
  username: string;
  firstName: "";
  lastName: "";
  permissions: string[];
  exp: Date;
  access_token: string;
  refresh_token: string;
  request_osbuk_user: boolean;
}

interface AuthContextModel {
  getUser: () => User;
  setToken: (input: AccessTokenDto, remember: boolean) => void;
  removeUserFromStorage: () => void;
}

let AuthContext = React.createContext<AuthContextModel>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  var user: User = {
    id: "",
    email: "",
    role: Role.USER,
    username: "",
    firstName: "",
    lastName: "",
    permissions: [],
    exp: new Date(),
    access_token: "",
    refresh_token: "",
    request_osbuk_user: false
  };

  const setUserFromStorage = () => {
    user.access_token = localStorage.getItem("access_token") || "";
    user.refresh_token = localStorage.getItem("refresh_token") || "";

    if (user.access_token.length === 0) {
      user.access_token = sessionStorage.getItem("access_token") || "";
    }

    if (user.refresh_token.length === 0) {
      user.refresh_token = sessionStorage.getItem("refresh_token") || "";
    }

    if (user.access_token.length > 0) {
      const decoded = jwtDecode(user.access_token) as any;

      user.id = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];

      user.username = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];

      user.id = decoded["id"]; // id'yi doğru şekilde almak
      user.email = decoded["email"]; // email'yi doğru şekilde almak
      //user.username = decoded["username"]; // username'yi doğru şekilde almak
      //user.role = decoded["role"];
      const role = decoded["role"];
      if (role === "admin") {
        user.role = Role.ADMIN;
      } else if (role === "osbuk_user") {
        user.role = Role.OSBUK_USER;
      } else {
        user.role = Role.USER;
      }
      user.firstName = decoded["first_name"];
      user.lastName = decoded["last_name"];
      user.request_osbuk_user = decoded["request_osbuk_user"];
      user.exp = new Date(decoded["exp"] * 1000);
    } else {
      user = {
        id: "",
        email: "",
        role: Role.USER,
        username: "",
        firstName: "",
        lastName: "",
        permissions: [],
        exp: new Date(),
        access_token: "",
        refresh_token: "",
        request_osbuk_user: false
      };
    }

    axios.defaults.headers.common["Authorization"] = "Bearer " + user.access_token;
  };

  const removeUserFromStorage = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");

    //localStorage.removeItem("userName");
    //localStorage.removeItem("role");
    //sessionStorage.removeItem("token");
    //sessionStorage.removeItem("userName");
    //sessionStorage.removeItem("role");
    setUserFromStorage();
  };

  const setToken = (input: AccessTokenDto, remember: boolean) => {
    if (remember) {
      if (input.access_token) {
        localStorage.setItem("access_token", input.access_token);
      }
      if (input.refresh_token) {
        localStorage.setItem("refresh_token", input.refresh_token);
      }
    } else {
      if (input.access_token) {
        sessionStorage.setItem("access_token", input.access_token);
      }
      if (input.refresh_token) {
        sessionStorage.setItem("refresh_token", input.refresh_token);
      }
    }

    setUserFromStorage();
  };

  const getUser = () => {
    setUserFromStorage();
    return user;
  };

  const value = { getUser, setToken, removeUserFromStorage };

  setUserFromStorage();

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return React.useContext(AuthContext);
}

export function HasPermission({ role, children }: { role: Role; children: JSX.Element }) {
  let auth = useAuth();
  let location = useLocation();
  var user = auth.getUser();
  const toast = useToast();

  if (user.access_token.length === 0) {
    toast.show("Öncelikle giriş yapmalısınız.", "error");
    return <Navigate to="/loginsebis" state={{ from: location }} replace />;
  }
  if (user.role === Role.ADMIN || user.role === role || (role === Role.USER && user.role === Role.OSBUK_USER)) {
    return children;
  } else {
    toast.show("Bu sayfa için erişim yetkiniz yok.", "error");
    return <Navigate to="/access-denied" state={{ from: location.pathname }} replace />;
  }

  return children;
}

// Yeni component: Kayıt olmayan kullanıcılar da sayfayı görebilir, ancak etkileşimli işlemler için login gerekir
export function PublicPageWrapper({ 
  children, 
  requireLoginForAction = false,
  actionMessage = "Bu işlemi gerçekleştirmek için giriş yapmalısınız."
}: { 
  children: JSX.Element; 
  requireLoginForAction?: boolean;
  actionMessage?: string;
}) {
  let auth = useAuth();
  let location = useLocation();
  var user = auth.getUser();
  const toast = useToast();

  // Eğer kullanıcı giriş yapmışsa, sayfayı göster
  if (user.access_token.length > 0) {
    return children;
  }

  // Eğer etkileşimli işlem gerekiyorsa ve kullanıcı giriş yapmamışsa
  if (requireLoginForAction) {
    toast.show(actionMessage, "error");
    return <Navigate to="/loginsebis" state={{ from: location }} replace />;
  }

  // Kayıt olmayan kullanıcılar sayfayı görebilir
  return children;
}
